<template>
  <!-- Payment Modal -->
  <Modal :open="stkModal" @closed="stkModal = false">
    <div class="modal-header bg-primary bg-opacity-10 border-bottom border-primary">
      <h5 class="modal-title m-0 d-flex align-items-center">
        <vue-feather type="credit-card" class="me-2" size="20"></vue-feather>
        M-PESA Payment
      </h5>
      <button @click="stkModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
      </button>
    </div>
    <div class="modal-body p-4">
      <div v-if="!paymentProcessing">
        <!-- Promo Banner -->
        <div role="alert" class="alert alert-info d-flex align-items-center mb-4">
          <vue-feather type="gift" class="me-2" size="18"></vue-feather>
          <div class="content">
            <strong>Special Offer:</strong> Get one month free when you subscribe for 12 months!
          </div>
        </div>

        <!-- Package Info -->
        <div class="card mb-4 bg-light">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">Selected Package</h6>
            <h5 class="card-title">{{ package_name }}</h5>
            <p class="card-text">Base Price: {{ formatNumber(price) }} per month</p>
          </div>
        </div>

        <!-- Payment Form -->
        <form @submit.prevent="renewSubscription" class="mb-3">
          <!-- Phone Number -->
          <div class="mb-4">
            <label for="phone" class="form-label fw-bold">Phone Number</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-phone"></i></span>
              <input
                type="tel"
                class="form-control"
                id="phone"
                v-model="phone"
                placeholder="07XXXXXXXX"
                :class="{'is-invalid': phoneError}"
                @input="validatePhone"
              >
              <div class="invalid-feedback" v-if="phoneError">
                {{ phoneError }}
              </div>
            </div>
            <small class="text-muted">Enter the M-PESA registered phone number</small>
          </div>

          <!-- Duration Selection -->
          <div class="mb-4">
            <label for="duration" class="form-label fw-bold">Subscription Duration</label>
            <select id="duration" class="form-select" v-model="duration" @change="calculateAmount" data="">
              <option value="1">1 Month</option>
              <option value="3">3 Months</option>
              <option value="6">6 Months</option>
              <option value="9">9 Months</option>
              <option value="12">12 Months (Get 1 Month Free)</option>
            </select>
          </div>

          <!-- Price Summary -->
          <div class="card border-0 bg-light">
            <div class="card-body">
              <div class="d-flex justify-content-between mb-2">
                <span>Base Price:</span>
                <span>{{ formatNumber(price) }} / month</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>Duration:</span>
                <span>{{ duration }} month{{ duration > 1 ? 's' : '' }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2" v-if="discount > 0">
                <span>Subtotal:</span>
                <span>{{ formatNumber(subtotal) }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2 text-success" v-if="discount > 0">
                <span>Discount:</span>
                <span>-{{ formatNumber(discount) }}</span>
              </div>
              <hr v-if="discount > 0">
              <div class="d-flex justify-content-between fw-bold">
                <span>Total Amount:</span>
                <span class="text-primary h5 mb-0">{{ formatNumber(amount) }}</span>
              </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Processing State -->
      <div v-else class="py-4">
        <div class="text-center mb-4">
          <Spinner stroke="#07b35d" size="75" spinner-class="mb-3" />
          <h4 class="mb-3">Processing Payment</h4>
          <div class="alert alert-info p-3">
            <vue-feather type="info" class="me-2" size="18"></vue-feather>
            Enter your M-PESA PIN when prompted on your phone
          </div>
          <p class="text-muted">Please do not close this window while the transaction is being processed.</p>
        </div>
      </div>
    </div>
    <div class="modal-footer bg-light">
      <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="stkModal = false" :disabled="paymentProcessing">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary d-flex align-items-center"
        :class="paymentProcessing || phoneError ? 'disabled' : ''"
        @click="renewSubscription"
      >
        <vue-feather type="check-circle" class="me-2" size="16" v-if="!paymentProcessing"></vue-feather>
        <span>{{ paymentProcessing ? 'Processing...' : 'Complete Payment' }}</span>
      </button>
    </div>
  </Modal>
  <!-- Payment Modal End -->
</template>

<script>
import subscriptionService from "@/api/subscriptionService";
import notify from "@/utils/notify";
import {decrypt} from "@/utils/crypto";
import formatNumber from "@/utils/formatNumber";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';
import Spinner from "@/components/spinner/Spinner.vue";

export default {
  name: "Stk-Modal",
  components: {
    VueFeather,
    Spinner
  },
  props: {
    package_id: {
      type: String,
      required: true
    },
    package_name: {
      type: String,
      required: true
    },
    price: {
      type: Number,
      required: true
    },
    type: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      stkModal: false,
      paymentProcessing: false,
      organization_id: '',
      duration: 1,
      amount: 0,
      phone: '',
      phoneError: '',
      discount: 0,
      subtotal: 0,
      organization: {},
      profile: {},
    }
  },

  methods: {
    formatNumber,

    stkModalOpen() {
      this.resetForm();
      this.stkModal = true;
    },

    resetForm() {
      this.duration = 1;
      this.paymentProcessing = false;
      this.phoneError = '';
      this.calculateAmount();
    },

    validatePhone() {
      // Basic validation for Kenyan phone numbers
      if (!this.phone) {
        this.phoneError = 'Phone number is required';
        return false;
      }

      // Remove any non-digit characters
      const cleanPhone = this.phone.replace(/\D/g, '');

      // Check if it's a valid format (assuming Kenyan format)
      if (!/^(07|01)\d{8}$/.test(cleanPhone)) {
        this.phoneError = 'Please enter a valid Kenyan phone number';
        return false;
      }

      this.phoneError = '';
      return true;
    },

    calculateAmount() {
      // Convert duration to number to ensure proper calculations
      const durationNum = parseInt(this.duration);

      // Calculate subtotal (before discount)
      this.subtotal = this.price * durationNum;

      // Apply discount for 12-month subscription (1 month free)
      if (durationNum === 12) {
        this.discount = this.price;
        this.amount = this.price * 11;
      } else {
        this.discount = 0;
        this.amount = this.subtotal;
      }
    },

    renewSubscription: async function (e) {
      // Validate phone before proceeding
      if (!this.validatePhone()) {
        return;
      }

      loadingButton(e);

      try {

        let response = await subscriptionService.initPayment({
          organization_id: this.organization.id,
          package_id: this.package_id,
          type: this.type,
          package: this.package_name,
          duration: this.duration,
          amount: this.amount,
          phone: this.phone
        });

        if (response.status === 201) {
          notify.alert.success('M-PESA payment initiated successfully');
          this.paymentProcessing = true;
          await this.checkPayment(response.data.id);
        } else {
          notify.alert.error('Failed to initiate payment. Please try again.');
        }
      } catch (error) {
        notify.alert.error('An error occurred: ' + (error.message || 'Unknown error'));
        this.paymentProcessing = false;
      }

      loadingButton(e, false);
    },

    async checkPayment(id) {
      try {
        let response = await subscriptionService.checkPayment({
          payment_details_id: id
        });

        if (response.status === 'Pending') {
          // Continue checking after delay
          setTimeout(() => this.checkPayment(id), 3000);
        } else if (response.status === 'Failed') {
          this.paymentProcessing = false;
          this.stkModal = false;
          notify.alert.error('Payment failed: ' + (response.description || 'Unknown error'), 7000);
        } else {
          // Payment successful
          notify.alert.success('Payment successful!');
          this.$router.push('/paymentSuccess');
        }
      } catch (error) {
        this.paymentProcessing = false;
        notify.alert.error('Error checking payment status: ' + (error.message || 'Unknown error'));
      }
    }
  },

  async created() {
    try {
      this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
      this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));

      // Format phone number if needed
      if (this.profile.phone) {
        this.phone = this.profile.phone || this.organization.phone;
        // Remove country code if present
        if (this.phone.startsWith('254')) {
          this.phone = '0' + this.phone.substring(3);
        }
      }

      this.calculateAmount();
    } catch (error) {
      console.error('Error initializing component:', error);
    }
  },

  watch: {
    duration: function () {
      this.calculateAmount();
    },
    price: function () {
      this.calculateAmount();
    }
  }
}
</script>
